/**
 * MotionCreateModal — "Create Motion - Step 1" dialog (V14 motion-centric).
 *
 * Figma source: PAGE "IVT" node 1956:31051 / 1956:30336.
 * Field order (per Figma, confirmed 2026-06-30): Motion Name → Industry →
 * Select Assets (Optional). NOTE this differs from pages-design.md §4.1's
 * Industry-first ordering — Figma wins on layout.
 *
 * DATA / OP decisions (skill Step 3 — never invent a command; reuse existing):
 *   - Industry universe = ListIndustries (already deployed; same source the
 *     AssetsPage/NewAssets flows use). We do NOT add a new industries op.
 *   - Asset multiselect = ONE ListAssets call scoped to origin=ivt_incubated (the
 *     IVT incubated Asset slice — same slice the New Assets tab manages), split
 *     client-side into two groups exactly like pages-design.md §4.1 note:
 *       · industry-anchored assets (AssetSummary.industry set) → shown only when
 *         they match the chosen Industry (local filter; re-groups instantly when
 *         Industry changes, no refetch).
 *       · topic-anchored assets (industry empty, AssetSummary.topic set) → always
 *         selectable, not industry-scoped.
 *     So changing Industry re-filters the options locally; already-selected assets
 *     that fall out of scope stay selected (the user chose them deliberately).
 *   - Submit = CreateMotionCommand({ industry, name, assetIds }). The backend
 *     DERIVES useCases / sub-topics from the chosen assets at read time — the
 *     dialog does NOT send them (pages-design.md §3.2b / §4.1). targetProfile /
 *     playbook / kpiTargets are not collected this slice (no UI for them).
 *
 * The fields, their load and the option logic live in components/MotionCreateForm, shared with
 * the STO dashboard's `+ Motion` dialog; this file owns the Modal and the submit.
 *
 * GUARD (decision #2): Industry is @required — Confirm is disabled until a
 * non-empty Motion Name AND an Industry are chosen. Select Assets is Optional.
 */

import { useState } from 'react';
import { denialHint } from '../../../utils/authorization';
import { Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { getIvtClient, CreateMotionCommand } from '../../../client/IvtHubClient';
import {
  MotionCreateFields,
  useMotionCreateForm,
} from '../../../components/MotionCreateForm/MotionCreateForm';

interface MotionCreateModalProps {
  onClose: () => void;
  /** Called with the new motionId after a successful create. */
  onCreated: (motionId: string) => void;
  /** When set, only these industries are selectable (permission-scoped). */
  allowedIndustries?: string[] | null;
}

export default function MotionCreateModal({ onClose, onCreated, allowedIndustries }: MotionCreateModalProps) {
  // The fields, their options and the load live in the shared form; this dialog adds the submit.
  const form = useMotionCreateForm({ allowedIndustries });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const error = submitError ?? form.loadError;

  // Industry is @required (decision #2) → no industry, no create. Assets optional.
  const canSubmit = !submitting && form.values !== null;

  const handleSubmit = async () => {
    if (!canSubmit || !form.values) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { name, industry, assetIds } = form.values;
      const out = await getIvtClient().send(
        // useCases / sub-topics are DERIVED server-side from these assetIds
        // (pages-design §3.2b) — we only send the chosen asset ids.
        new CreateMotionCommand({ name, industry, assetIds }),
      );
      const newId = out.motion?.motionId;
      onClose();
      if (newId) onCreated(newId);
    } catch (e) {
      // CreateMotion is the one op that can produce BOTH 403s — the gate's
      // write-role denial and its own modelled industry denial. Their names are
      // identical, so `denialHint` splits them on `reason` and gives the right
      // remedy (request ivthub-editor vs request the industry).
      setSubmitError(denialHint(e) ?? ((e as Error).message || '创建失败'));
      setSubmitting(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!submitting) onClose();
      }}
      header="Create Motion - Step 1"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={submitting}
              disabled={!canSubmit}
            >
              Confirm
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      {form.loading ? (
        <Box padding={{ vertical: 'l' }} textAlign="center">
          加载中…
        </Box>
      ) : (
        <SpaceBetween size="l">
          {error && (
            <Box color="text-status-error" data-testid="motion-create-error">
              ⚠️ {error}
            </Box>
          )}
          <MotionCreateFields form={form} disabled={submitting} />
        </SpaceBetween>
      )}
    </Modal>
  );
}
